import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  ConfirmDialog,
  DateRangeFilter,
  InputSearch,
  Pagination,
  Status,
  TableData,
  UniversalFilter,
} from '@/components';
import { Column } from '@/types/common';
import {
  CollectionPurpose,
  CollectionStatus,
  COLLECTION_PURPOSE_LABEL,
  RentalCollection,
} from '@/types/rental';
import {
  cancelRentalPayment,
  getRentalCollections,
  retryRefundPayout,
  simulateCollectionPayment,
} from '@/services/api/rentals';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  canShowDevAffordances,
  extractApiErrorMessage,
  formatDateTime,
  formatRupiah,
} from '@/utils';
import {
  canCancelPayment,
  canRetryRefund,
  collectionVoucherLabel,
  refundStatusLabel,
  refundToneClass,
} from '../rentalDisplay';

interface PaymentHistoryTabProps {
  rentalId: string;
  /**
   * Called after a simulated settlement, so the parent can re-read the rental —
   * a capture moves prepaid balance, arrears and escalation, none of which live
   * on this tab.
   */
  onPaymentSettled?: () => void;
  /**
   * Buka tab Rincian Tagihan yang disempitkan ke hari-hari yang dibayar satu
   * transaksi. Absen berarti kolom "No. Transaksi" tetap teks biasa.
   */
  onViewSettledCharges?: (collection: RentalCollection) => void;
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface CollectionFilters extends Record<string, any> {
  status?: CollectionStatus[];
  purpose?: CollectionPurpose[];
}

const STATUS_OPTIONS = [
  { key: 'paid', label: 'Success', value: CollectionStatus.PAID },
  { key: 'pending', label: 'Pending', value: CollectionStatus.PENDING },
  { key: 'failed', label: 'Failed', value: CollectionStatus.FAILED },
  { key: 'expired', label: 'Expired', value: CollectionStatus.EXPIRED },
  // A method switch or a called-off booking — see CollectionStatus.CANCELLED.
  { key: 'cancelled', label: 'Cancelled', value: CollectionStatus.CANCELLED },
];

const PURPOSE_OPTIONS = Object.values(CollectionPurpose).map((purpose) => ({
  key: purpose.toLowerCase(),
  label: COLLECTION_PURPOSE_LABEL[purpose],
  value: purpose,
}));

/**
 * Payment history for one rental, from `GET /rental/:id/collections`.
 *
 * Server-paginated and server-filtered — the rows used to ride along on the
 * rental detail as `payments[]`, which grew one entry per day for the life of
 * the rental.
 */
export const PaymentHistoryTab: React.FC<PaymentHistoryTabProps> = ({
  rentalId,
  onPaymentSettled,
  onViewSettledCharges,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });
  const [activeFilters, setActiveFilters] = useState<CollectionFilters>({});
  const [rows, setRows] = useState<RentalCollection[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [simulatingId, setSimulatingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<RentalCollection | null>(
    null
  );
  const [isCancelling, setIsCancelling] = useState(false);
  const cancelLabel = cancelTarget?.code || cancelTarget?.id || '';

  // Shown only where the build declares itself non-production. The endpoint is
  // gated server-side too (403 on production), so this only keeps a button
  // that cannot work off the ops screen.
  const canSimulate = canShowDevAffordances();

  const filterConfigs = useMemo(
    () => [
      {
        key: 'status',
        title: 'Status',
        icon: 'ri-checkbox-circle-line',
        multiple: true,
        staticOptions: STATUS_OPTIONS,
      },
      {
        key: 'purpose',
        title: 'Tujuan',
        icon: 'ri-price-tag-3-line',
        multiple: true,
        staticOptions: PURPOSE_OPTIONS,
      },
    ],
    []
  );

  const fetchCollections = useCallback(
    async (
      page: number,
      size: number,
      search: string,
      filters: CollectionFilters,
      range: DateRange
    ) => {
      setIsLoading(true);
      try {
        const response = await getRentalCollections(rentalId, {
          page,
          size,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(filters.status?.length ? { status: filters.status } : {}),
          ...(filters.purpose?.length ? { purpose: filters.purpose } : {}),
          ...(range.from
            ? { dateFrom: dayjs(range.from).format('YYYY-MM-DD') }
            : {}),
          ...(range.to ? { dateTo: dayjs(range.to).format('YYYY-MM-DD') } : {}),
        });

        setRows(response?.items || []);
        setTotal(response?.pagination?.total || 0);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load payment history')
        );
        setRows([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [rentalId]
  );

  const reload = useCallback(() => {
    fetchCollections(currentPage, pageSize, searchTerm, activeFilters, dateRange);
  }, [
    fetchCollections,
    currentPage,
    pageSize,
    searchTerm,
    activeFilters,
    dateRange,
  ]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * Dev/QA: settle a PENDING collection without paying. Under
   * `RENT_PAYMENT_MODE=core` the real capture webhook fires, so the rental's
   * balance and escalation move too — hence the parent reload.
   */
  const handleSimulate = async (collection: RentalCollection) => {
    setSimulatingId(collection.id);
    try {
      await simulateCollectionPayment(collection.id);
      showSuccessToast(
        'Success',
        `Pembayaran ${collection.code || collection.id} disimulasikan`
      );
      reload();
      onPaymentSettled?.();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal simulasi pembayaran')
      );
    } finally {
      setSimulatingId(null);
    }
  };

  /**
   * Re-instruct a stuck or refused refund — not dev/QA, unlike the simulate
   * above. The backend refuses every other payout state, and a failure comes
   * back with the destination's own reason.
   */
  const handleRetryRefund = async (
    collection: RentalCollection,
    payoutID: string
  ) => {
    setRetryingId(collection.id);
    try {
      await retryRefundPayout(payoutID);
      showSuccessToast(
        'Success',
        `Refund ${collection.code || collection.id} diinstruksikan ulang`
      );
      reload();
      onPaymentSettled?.();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal instruksikan ulang refund')
      );
    } finally {
      setRetryingId(null);
    }
  };

  /** Withdraws a rent payment the driver has not paid; the voucher goes back. */
  const handleCancelPayment = async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    try {
      await cancelRentalPayment(rentalId, cancelTarget.id);
      showSuccessToast('Success', `Pembayaran ${cancelLabel} dibatalkan`);
      setCancelTarget(null);
      reload();
      onPaymentSettled?.();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal membatalkan pembayaran')
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const columns: Column<RentalCollection>[] = [
    {
      key: 'code',
      title: 'No. Transaksi',
      // Tautan ke ledger hanya untuk pembayaran yang benar-benar menutup hari:
      // yang PENDING atau gagal tidak menyelesaikan apa pun, jadi tautannya
      // akan selalu mendarat di daftar kosong.
      render: (value: string, record: RentalCollection) => {
        const label = value || record.id;
        if (!onViewSettledCharges || record.status !== CollectionStatus.PAID) {
          return <span className="text-[14px] text-gray-900">{label}</span>;
        }
        return (
          <button
            type="button"
            onClick={() => onViewSettledCharges(record)}
            className="inline-flex items-center gap-1 text-[14px] font-medium text-primary underline hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-primary rounded"
            title="Lihat hari yang dibayar transaksi ini"
          >
            {label}
            <i className="ri-arrow-right-up-line text-[14px]"></i>
          </button>
        );
      },
    },
    {
      key: 'createdAt',
      title: 'Tanggal',
      // Shows createdAt, which is also what the date range filters on — paidAt
      // is null for anything that did not settle.
      render: (_value: unknown, record: RentalCollection) => (
        <span className="text-[14px] text-gray-900">
          {formatDateTime(record.createdAt)}
        </span>
      ),
    },
    {
      key: 'webhookEventId',
      title: 'Ref. PSP',
      render: (value: string | null) =>
        value ? (
          <span
            className="text-[13px] font-mono text-gray-700 max-w-[160px] truncate inline-block align-middle"
            title={value}
          >
            {value}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'method',
      title: 'Metode',
      render: (value: string) => (
        <span className="text-[14px] text-gray-900">{value || '-'}</span>
      ),
    },
    {
      key: 'purpose',
      title: 'Tujuan',
      render: (value: CollectionPurpose) => (
        <span className="text-[14px] text-gray-900">
          {COLLECTION_PURPOSE_LABEL[value] || value || '-'}
        </span>
      ),
    },
    {
      key: 'amount',
      title: 'Jumlah',
      render: (value: number, record: RentalCollection) => {
        const voucher = collectionVoucherLabel(record);
        return (
          <div className="leading-tight">
            <div className="text-[14px] font-medium text-gray-900">
              {formatRupiah(value, '-', { space: false })}
            </div>
            {voucher ? (
              <div className="text-[12px] text-gray-500">{voucher}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      render: (value: string) =>
        value ? (
          <Status value={value} type="rentalCollection" size="medium" />
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    // Beside the collection's own status, which says whether the driver paid.
    // A payout that was never instructed leaves no trace there.
    {
      key: 'refund',
      title: 'Refund',
      render: (_value: unknown, record: RentalCollection) => {
        const refund = record.refund;
        if (!refund) return <span className="text-gray-400">-</span>;
        return (
          <div className="leading-tight">
            <span
              className={`inline-block rounded px-2 py-0.5 text-[12px] font-medium ${refundToneClass(
                refund.payoutStatus
              )}`}
            >
              {refundStatusLabel(refund.payoutStatus)}
            </span>
            <div className="mt-1 text-[12px] text-gray-500">
              {formatRupiah(refund.amount, '-', { space: false })}
            </div>
          </div>
        );
      },
    },
    {
      key: 'retry-refund',
      title: '',
      align: 'right' as const,
      render: (_value: unknown, record: RentalCollection) => {
        const { refund } = record;
        return canRetryRefund(refund) ? (
          <span title="Kirim ulang instruksi payout ke core-payment">
            <Button
              variant="white"
              size="small"
              onClick={() => handleRetryRefund(record, refund.payoutID)}
              disabled={retryingId !== null}
            >
              {retryingId === record.id ? 'Memproses...' : 'Refund Ulang'}
            </Button>
          </span>
        ) : null;
      },
    },
    {
      key: 'cancel-payment',
      title: '',
      align: 'right' as const,
      render: (_value: unknown, record: RentalCollection) =>
        canCancelPayment(record) ? (
          <Button
            variant="white"
            size="small"
            onClick={() => setCancelTarget(record)}
          >
            Batalkan
          </Button>
        ) : null,
    },
    // Only a PENDING collection can be simulated — the endpoint 409s on any
    // other status, so there is nothing to offer on settled rows.
    ...(canSimulate
      ? [
          {
            key: 'simulate',
            title: '',
            align: 'right' as const,
            render: (_value: unknown, record: RentalCollection) =>
              record.status === CollectionStatus.PENDING ? (
                <span title="Dev/QA: lunasi tanpa bayar">
                  <Button
                    variant="white"
                    size="small"
                    onClick={() => handleSimulate(record)}
                    disabled={simulatingId !== null}
                  >
                    {simulatingId === record.id
                      ? 'Memproses...'
                      : 'Simulasi Bayar'}
                  </Button>
                </span>
              ) : null,
          } as Column<RentalCollection>,
        ]
      : []),
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <DateRangeFilter
          onDateRangeChange={(range) => {
            setDateRange(range);
            setCurrentPage(1);
          }}
          initialRange={dateRange}
        />

        <div className="flex items-center gap-2">
          <InputSearch
            placeholder="Search..."
            value={searchInput}
            onChange={setSearchInput}
            onSearch={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            onClear={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            debounceMs={300}
            size="small"
          />

          <UniversalFilter
            filters={filterConfigs}
            currentFilters={activeFilters}
            onApply={(filters: CollectionFilters) => {
              setActiveFilters(filters);
              setCurrentPage(1);
            }}
            onClear={() => {
              setActiveFilters({});
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-gray-600">Loading payment history...</span>
        </div>
      ) : (
        <TableData
          data={rows}
          columns={columns}
          size="small"
          className="min-w-full"
          showHeaderWhenEmpty
          emptyState={
            <div className="py-16">
              <div className="w-[148px] h-[148px] mx-auto mb-4">
                <img
                  src="/images/no-results.png"
                  alt="Belum ada pembayaran"
                  className="w-full h-full"
                />
              </div>
              <h3
                className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] text-gray-900 mb-2 text-center"
                style={{ letterSpacing: '-1.5%' }}
              >
                Belum Ada Riwayat Pembayaran
              </h3>
              <p
                className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-gray-500 text-center max-w-[400px] mx-auto"
                style={{ letterSpacing: '-0.6%' }}
              >
                Transaksi pembayaran mitra akan muncul di sini.
              </p>
            </div>
          }
        />
      )}

      <ConfirmDialog
        isOpen={cancelTarget !== null}
        title="Batalkan pembayaran?"
        description={`Pembayaran ${cancelLabel} belum dibayar. Setelah dibatalkan, mitra bisa membuat pembayaran baru dan voucher yang dipakai kembali ke mitra.`}
        confirmLabel="Batalkan Pembayaran"
        cancelLabel="Kembali"
        confirmVariant="danger"
        onConfirm={handleCancelPayment}
        onCancel={() => setCancelTarget(null)}
        isBusy={isCancelling}
      />

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default PaymentHistoryTab;
